import {
  CASE_DOCUMENT_CATEGORIES,
  CASE_DOCUMENT_CATEGORY_LABELS,
  type CaseDocumentCategory,
  CLIENT_DOCUMENT_KIND_LABELS,
  CLIENT_DOCUMENT_KINDS,
  type ClientDocumentDto,
  type ClientDocumentKind,
  type ClientDocumentsDto,
  clientDocumentMatches,
  DOCUMENT_ACCEPT,
  DOCUMENT_MAX_BYTES,
  DOCUMENT_TYPES,
  documentExtension,
} from '@bqcrm/shared';
import { ActionIcon, Anchor, Badge, Button, Card, FileButton, Group, Modal, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconBriefcase, IconFile, IconFileText, IconFolderOpen, IconMessages, IconPhoto, IconPin, IconReceipt, IconSearch, IconSignature, IconTrash, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api, apiForm } from '../../api';
import { ask } from '../../confirm';
import { QueryState } from '../../components/QueryState';
import { EmptyState, FilterChips, SectionCard } from '../../components/ui';
import { formatDateTime, plural } from '../../format';
import { notifyError, notifyOk } from '../../notify';

const KIND_ICON: Record<ClientDocumentKind, typeof IconFile> = {
  umowa: IconSignature,
  aneks: IconSignature,
  oferta: IconFileText,
  protokol: IconFileText,
  faktura: IconReceipt,
  dokument: IconFile,
  zdjecie: IconPhoto,
  korespondencja: IconMessages,
};

const NO_CASE = '__bez__';

function DocRow({ d, cases, onPin, onDelete }: { d: ClientDocumentDto; cases: { id: string }[]; onPin: (d: ClientDocumentDto) => void; onDelete: (d: ClientDocumentDto) => void }) {
  const Icon = KIND_ICON[d.kind];
  return (
    <Group px="md" py={6} gap="sm" wrap="nowrap" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Tooltip label={CLIENT_DOCUMENT_KIND_LABELS[d.kind]} withArrow>
        <Icon size={18} style={{ flexShrink: 0 }} aria-label={CLIENT_DOCUMENT_KIND_LABELS[d.kind]} />
      </Tooltip>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Anchor href={`/api${d.url}`} target="_blank" rel="noreferrer" size="sm" fw={500} style={{ overflowWrap: 'anywhere' }}>
          {d.title}
        </Anchor>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {[d.detail, formatDateTime(d.at)].filter(Boolean).join(' · ')}
        </Text>
      </div>
      {d.partyDocumentId && cases.length > 0 && (
        <Tooltip label="Przypnij do sprawy">
          <ActionIcon variant="subtle" aria-label="Przypnij do sprawy" onClick={() => onPin(d)}>
            <IconPin size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {d.canDelete && d.partyDocumentId && (
        <ActionIcon variant="subtle" color="red" aria-label="Usuń dokument" onClick={() => onDelete(d)}>
          <IconTrash size={16} />
        </ActionIcon>
      )}
    </Group>
  );
}

function AddDocumentModal({ partyId, data, onClose }: { partyId: string; data: ClientDocumentsDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [target, setTarget] = useState<string>(NO_CASE);
  const [category, setCategory] = useState<CaseDocumentCategory>('inne');
  const [description, setDescription] = useState('');
  const error = file && !DOCUMENT_TYPES[documentExtension(file.name)] ? 'Nieobsługiwany typ pliku' : file && file.size > DOCUMENT_MAX_BYTES ? 'Plik jest za duży' : null;
  const save = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('category', category);
      form.append('description', description);
      return apiForm(target === NO_CASE ? `/parties/${partyId}/documents` : `/cases/${target}/documents`, form);
    },
    onSuccess: () => {
      notifyOk(target === NO_CASE ? 'Dodano dokument klienta' : 'Dodano dokument do sprawy');
      void qc.invalidateQueries({ queryKey: ['party-documents', partyId] });
      if (target !== NO_CASE) void qc.invalidateQueries({ queryKey: ['case-documents', target] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Nowy dokument klienta" size="md">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <FileButton accept={DOCUMENT_ACCEPT} onChange={setFile}>
            {(props) => (
              <Button {...props} variant="default" leftSection={<IconUpload size={16} />}>
                Wybierz plik
              </Button>
            )}
          </FileButton>
          <Text size="sm" c={error ? 'red' : 'dimmed'} truncate>
            {file ? (error ? `${file.name} — ${error}` : file.name) : 'Nie wybrano pliku'}
          </Text>
        </Group>
        <Select
          label="Sprawa"
          value={target}
          onChange={(v) => setTarget(v ?? NO_CASE)}
          allowDeselect={false}
          data={[{ value: NO_CASE, label: 'Bez sprawy (dokument klienta)' }, ...data.cases.map((c) => ({ value: c.id, label: `${c.number} — ${c.title}` }))]}
        />
        <Select label="Rodzaj" value={category} onChange={(v) => setCategory((v ?? 'inne') as CaseDocumentCategory)} allowDeselect={false} data={CASE_DOCUMENT_CATEGORIES.map((k) => ({ value: k, label: CASE_DOCUMENT_CATEGORY_LABELS[k] }))} />
        <TextInput label="Opis" value={description} onChange={(e) => setDescription(e.currentTarget.value)} maxLength={500} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!file || !!error} loading={save.isPending} onClick={() => save.mutate()}>
            Dodaj dokument
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PinModal({ doc, data, partyId, onClose }: { doc: ClientDocumentDto; data: ClientDocumentsDto; partyId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [caseId, setCaseId] = useState<string | null>(data.cases[0]?.id ?? null);
  const pin = useMutation({
    mutationFn: () => api(`/party-documents/${doc.partyDocumentId}/pin`, { method: 'POST', body: { caseId } }),
    onSuccess: () => {
      notifyOk('Dokument przypięto do sprawy');
      void qc.invalidateQueries({ queryKey: ['party-documents', partyId] });
      void qc.invalidateQueries({ queryKey: ['case-documents', caseId] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Przypięcie dokumentu do sprawy" size="md">
      <Stack gap="sm">
        <Text size="sm">
          „{doc.title}” trafi do dokumentów wybranej sprawy (z tym samym rodzajem i opisem) i zniknie z grupy „Bez sprawy”.
        </Text>
        <Select label="Sprawa" value={caseId} onChange={setCaseId} allowDeselect={false} data={data.cases.map((c) => ({ value: c.id, label: `${c.number} — ${c.title}` }))} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!caseId} loading={pin.isPending} onClick={() => pin.mutate()}>
            Przypnij
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Zakładka „Dokumenty” w kartotece klienta (docs/21): wszystko ze spraw klienta pogrupowane po sprawach, wyszukiwanie całościowe. */
export function PartyDocuments({ partyId }: { partyId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['party-documents', partyId], queryFn: () => api<ClientDocumentsDto>(`/parties/${partyId}/documents`) });
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<ClientDocumentKind | 'wszystkie'>('wszystkie');
  const [adding, setAdding] = useState(false);
  const [pinning, setPinning] = useState<ClientDocumentDto | null>(null);
  const remove = useMutation({
    mutationFn: (d: ClientDocumentDto) => api(`/party-documents/${d.partyDocumentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto dokument');
      void qc.invalidateQueries({ queryKey: ['party-documents', partyId] });
    },
    onError: notifyError,
  });

  const groups = useMemo(
    () =>
      (q.data?.groups ?? [])
        .map((g) => ({ ...g, documents: g.documents.filter((d) => (kind === 'wszystkie' || d.kind === kind) && clientDocumentMatches(d, g.case?.number ?? null, search)) }))
        .filter((g) => g.documents.length > 0 || (!search && kind === 'wszystkie' && g.case === null && q.data?.canAdd)),
    [q.data, search, kind],
  );
  const counts = useMemo(() => {
    const m = new Map<ClientDocumentKind, number>();
    for (const g of q.data?.groups ?? []) for (const d of g.documents) m.set(d.kind, (m.get(d.kind) ?? 0) + 1);
    return m;
  }, [q.data]);
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  const found = groups.reduce((a, g) => a + g.documents.length, 0);

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <>
          <Group mb="sm" gap="sm" wrap="wrap">
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Szukaj we wszystkich dokumentach klienta (numer, nazwa pliku, opis, sprawa)"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            {q.data.canAdd && (
              <Button leftSection={<IconUpload size={16} />} onClick={() => setAdding(true)}>
                Dodaj dokument
              </Button>
            )}
          </Group>
          <FilterChips
            value={kind}
            onChange={setKind}
            options={[
              { value: 'wszystkie' as const, label: `Wszystkie (${total})` },
              ...CLIENT_DOCUMENT_KINDS.filter((k) => counts.get(k)).map((k) => ({ value: k, label: `${CLIENT_DOCUMENT_KIND_LABELS[k]} (${counts.get(k)})` })),
            ]}
          />
          {q.data.hiddenKinds.length > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              Bez uprawnień nie widać: {q.data.hiddenKinds.map((k) => CLIENT_DOCUMENT_KIND_LABELS[k].toLowerCase()).join(', ')}.
            </Text>
          )}
          {(search || kind !== 'wszystkie') && (
            <Text size="sm" c="dimmed" mt="xs">
              Znaleziono {found} {plural(found, 'dokument', 'dokumenty', 'dokumentów')}
            </Text>
          )}
          <Stack gap="md" mt="md">
            {groups.length === 0 && (
              <Card withBorder>
                <EmptyState icon={<IconFolderOpen size={24} />} title={search || kind !== 'wszystkie' ? 'Nic nie pasuje do wyszukiwania' : 'Brak dokumentów klienta'} />
              </Card>
            )}
            {groups.map((g) => (
              <SectionCard
                key={g.case?.id ?? NO_CASE}
                icon={g.case ? <IconBriefcase size={16} /> : <IconFolderOpen size={16} />}
                title={
                  g.case ? (
                    <Group gap={6} wrap="wrap">
                      <Anchor component={Link} to={`/sprawy/${g.case.id}?zakladka=dokumenty`} fw={600}>
                        {g.case.number}
                      </Anchor>
                      <Text span size="sm">
                        {g.case.title}
                      </Text>
                      {g.case.closed && (
                        <Badge size="sm" variant="light" color="gray">
                          Zamknięta
                        </Badge>
                      )}
                    </Group>
                  ) : (
                    'Bez sprawy'
                  )
                }
                actions={
                  <Text size="xs" c="dimmed">
                    {g.documents.length} {plural(g.documents.length, 'dokument', 'dokumenty', 'dokumentów')}
                  </Text>
                }
                padding={0}
              >
                {g.documents.length === 0 && <EmptyState compact title="Brak dokumentów bez sprawy" hint="Tu trafiają dokumenty klienta niezwiązane ze sprawą i załączniki nieprzypiętej korespondencji." />}
                {g.documents.map((d) => (
                  <DocRow
                    key={d.key}
                    d={d}
                    cases={q.data.cases}
                    onPin={setPinning}
                    onDelete={(x) => void ask(`Dokument „${x.title}” zniknie z kartoteki klienta.`, { title: 'Usunąć dokument?', confirm: 'Usuń', danger: true }).then((ok) => ok && remove.mutate(x))}
                  />
                ))}
              </SectionCard>
            ))}
          </Stack>
          {adding && <AddDocumentModal partyId={partyId} data={q.data} onClose={() => setAdding(false)} />}
          {pinning && <PinModal doc={pinning} data={q.data} partyId={partyId} onClose={() => setPinning(null)} />}
        </>
      )}
    </QueryState>
  );
}
